"use client";

import { Card, CircularProgress } from "@mui/material";
import { Formik } from "formik";
import React, { useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetServiceInterfaceLogTypes from "@/hooks/requests/service-interface-log/use-get-service-interface-log-types";
import useGetServiceInterfaceLogs from "@/hooks/requests/service-interface-log/use-get-service-interface-logs";
import useFilters from "@/hooks/use-filters";
import usePagination from "@/hooks/use-pagination";
import { IServiceInterfaceLog } from "@/interfaces/service-interface.interface";
import { ServiceInterfaceMessages } from "@/messages/service-interface/service-interface.messages";

import FiltersForm from "./components/FiltersForm";
import { ServiceInterfaceLogTable } from "./components/Table";
import { initialLogFilterValues } from "./constants";

type LocationState = {
  platformDisplayName?: string;
  serviceKey?: string;
};

type Props = {
  messages: ServiceInterfaceMessages;
};

const FIRST_PAGE = 0;
const LOADING_SPINNER_SIZE = 50;
const CARD_MARGIN_TOP = 16;

function ServiceInterfaceLogsPage({ messages }: Props) {
  const { serviceInterfaceId: serviceInterfaceIdParam } = useParams<{
    serviceInterfaceId: string;
  }>();
  const serviceInterfaceId = Number(serviceInterfaceIdParam);
  const { lang } = useLanguage();
  const location = useLocation();
  const locationState = (location.state as LocationState) ?? {};

  const [hasSearched, setHasSearched] = useState(false);

  const pagination = usePagination();
  const { pageNumber, pageSize, changePage } = pagination;
  const { filters, changeFilters, clearFilters } = useFilters<IServiceInterfaceLog>({
    initialValue: initialLogFilterValues,
  });

  const { data: logTypesData } = useGetServiceInterfaceLogTypes();
  const logTypes = logTypesData ?? [];

  const { data, isLoading, error } = useGetServiceInterfaceLogs({
    serviceInterfaceId,
    filters,
    pageNumber,
    pageSize,
    enabled: hasSearched && !Number.isNaN(serviceInterfaceId),
  });

  const subtitle = [locationState.platformDisplayName, locationState.serviceKey]
    .filter(Boolean)
    .join(" · ");

  const BREAD_CRUMBS = [
    { title: messages.breadCrumbIntegrations, href: ROUTE_URLS.integrations(lang) },
  ];

  const shouldLoadTable = hasSearched && !!data && !isLoading && !error && data.items.length > 0;
  const shouldLoadError = hasSearched && !isLoading && !!error;
  const shouldLoadNoLogs =
    hasSearched && !!data && !isLoading && !error && data.items.length === 0;

  const handleClearAndReset = () => {
    clearFilters();
    setHasSearched(false);
  };

  return (
    <PageLayout
      messages={messages}
      breadCrumbs={BREAD_CRUMBS}
      title={subtitle ? `${messages.logsPageTitle} — ${subtitle}` : messages.logsPageTitle}
      hideBoldTitle
    >
      <PageContainer>
        <Formik
          initialValues={filters}
          validateOnChange={false}
          onSubmit={(newFilters) => {
            changePage(FIRST_PAGE);
            changeFilters(newFilters);
            setHasSearched(true);
          }}
        >
          <FiltersForm
            messages={messages}
            logTypes={logTypes}
            onClear={handleClearAndReset}
          />
        </Formik>

        <Card sx={({ functions: { pxToRem } }) => ({ marginTop: pxToRem(CARD_MARGIN_TOP) })}>
          <Suspense isSuspended={hasSearched && isLoading} fallback={<CircularProgress size={LOADING_SPINNER_SIZE} />}>
            <MDBox p={2}>
              <MDBox p={2}>
                <MDTypography variant="h6" fontWeight="bold">
                  {messages.logsTableTitle}
                </MDTypography>
              </MDBox>

              {!hasSearched && (
                <MDBox px={2} pb={2}>
                  <MDTypography variant="body2" color="text">
                    {messages.filterHint}
                  </MDTypography>
                </MDBox>
              )}

              {shouldLoadTable && (
                <ServiceInterfaceLogTable
                  pagination={pagination}
                  data={data}
                  messages={messages}
                  logTypes={logTypes}
                />
              )}

              {shouldLoadError && (
                <AlertBlock
                  title={messages.errorToLoadLogs}
                  description={error.message}
                  alt={messages.noLogsFoundAlt}
                />
              )}

              {shouldLoadNoLogs && (
                <AlertBlock
                  title={messages.noLogsFoundError}
                  description={messages.noLogsFoundDescription}
                  alt={messages.noLogsFoundAlt}
                />
              )}
            </MDBox>
          </Suspense>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}

export default ServiceInterfaceLogsPage;
